/* eslint-disable prettier/prettier */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  setDoc,
  updateDoc,
  Timestamp,
  writeBatch,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * Utility to test whether a task template should run on the given JS Date.
 * @param {object} template - taskTemplate Firestore doc data.
 * @param {Date} date - JS Date object (local).
 * @returns {boolean}
 */
const templateMatchesDate = (template, date) => {
  if (!template?.schedule) return false;
  const { repeat, daysOfWeek } = template.schedule;
  const dayStr = date.toLocaleDateString("en-US", { weekday: "short" }); // Mon, Tue, etc.

  if (repeat === "daily") return true;
  if (repeat === "weekly" && Array.isArray(daysOfWeek)) {
    return daysOfWeek.includes(dayStr);
  }
  return false;
};

/**
 * Fetch all task templates for a care profile.
 * @param {string} careProfileId
 */
export const fetchTaskTemplates = async (careProfileId) => {
  const ref = collection(db, `careProfiles/${careProfileId}/taskTemplates`);
  const snap = await getDocs(ref);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Fetch task logs for a care profile & date (YYYY-MM-DD).
 * @param {string} careProfileId
 * @param {string} dateStr - YYYY-MM-DD
 */
export const fetchTaskLogsForDate = async (careProfileId, dateStr) => {
  const ref = collection(db, `careProfiles/${careProfileId}/taskLogs`);
  const q = query(ref, where("date", "==", dateStr));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Generate daily task logs for a given date if they don't exist.
 * @param {object} params
 *  - careProfileId
 *  - date (JS Date)
 *  - templates (array of template objects) optional; if omitted fetches internally
 *  - currentUser { uid, displayName, email }
 */
export const generateDailyTaskLogs = async ({
  careProfileId,
  date,
  templates,
  currentUser,
}) => {
  if (!careProfileId || !date || !currentUser) throw new Error("Missing params");

  // Use LOCAL date (not UTC) to avoid off-by-one issues near midnight/timezones
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const dateStr = `${yyyy}-${mm}-${dd}`; // YYYY-MM-DD in local time
  const yyyymmdd = `${yyyy}${mm}${dd}`;

  if (!templates) templates = await fetchTaskTemplates(careProfileId);

  const batch = writeBatch(db);
  let createdCount = 0;

  for (const tpl of templates) {
    if (!templateMatchesDate(tpl, date)) continue;

    const logId = `${yyyymmdd}_${tpl.id}`;
    const logRef = doc(db, `careProfiles/${careProfileId}/taskLogs/${logId}`);

    // Check existence
    // eslint-disable-next-line no-await-in-loop
    const existing = await getDoc(logRef);
    if (existing.exists()) continue;

    const checklist = (tpl.checklist || []).map((c) => {
      if (typeof c === "string") {
        return {
          text: c,
          completed: false,
          completedByUid: null,
          completedAt: null,
        };
      }
      // if already object, copy text property
      return {
        text: c.text || "",
        completed: false,
        completedByUid: null,
        completedAt: null,
      };
    });

    batch.set(logRef, {
      date: dateStr,
      templateId: tpl.id,
      title: tpl.title,
      description: tpl.description,
      scheduleSnapshot: tpl.schedule,
      checklist,
      createdBy: {
        uid: currentUser.uid,
        name: currentUser.displayName || currentUser.email || "",
      },
      createdAt: Timestamp.now(),
      lastModifiedBy: {
        uid: currentUser.uid,
        name: currentUser.displayName || currentUser.email || "",
      },
      lastModifiedAt: Timestamp.now(),
    });
    createdCount += 1;
  }

  if (createdCount > 0) await batch.commit();
  return createdCount;
};

/**
 * Generate task logs for a date range (inclusive), up to 30 days ahead.
 * Validates range length and clamps to 30 days.
 * @param {object} params
 *  - careProfileId
 *  - startDate (JS Date)
 *  - endDate (JS Date)
 *  - currentUser
 */
export const generateTaskLogsForRange = async ({
  careProfileId,
  startDate,
  endDate,
  currentUser,
}) => {
  if (!careProfileId || !startDate || !endDate || !currentUser)
    throw new Error("Missing params");

  // Normalize to local midnight boundaries
  const s = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const e = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  if (e < s) throw new Error("End date must be on/after start date");

  // Enforce max 30 days forward window
  const maxE = new Date(s);
  maxE.setDate(maxE.getDate() + 29); // inclusive window of 30 days
  const finalEnd = e > maxE ? maxE : e;

  // Fetch templates once
  const templates = await fetchTaskTemplates(careProfileId);

  let totalCreated = 0;
  // Iterate inclusive of finalEnd by comparing Y-M-D
  for (let d = new Date(s.getTime()); ; d.setDate(d.getDate() + 1)) {
    // eslint-disable-next-line no-await-in-loop
    const created = await generateDailyTaskLogs({
      careProfileId,
      date: new Date(d.getTime()),
      templates,
      currentUser,
    });
    totalCreated += created;
    const sameY = d.getFullYear() === finalEnd.getFullYear();
    const sameM = d.getMonth() === finalEnd.getMonth();
    const sameD = d.getDate() === finalEnd.getDate();
    if (sameY && sameM && sameD) break;
  }
  return totalCreated;
};

/**
 * Toggle checklist completion for a given log item index.
 * Caller must have write permission per security rules.
 */
export const toggleChecklistItem = async ({
  careProfileId,
  logId,
  itemIndex,
  completed,
  currentUser,
}) => {
  const logRef = doc(db, `careProfiles/${careProfileId}/taskLogs/${logId}`);
  const logSnap = await getDoc(logRef);
  if (!logSnap.exists()) throw new Error("Task log not found");
  const data = logSnap.data();
  if (!Array.isArray(data.checklist) || itemIndex >= data.checklist.length)
    throw new Error("Invalid checklist index");

  const newChecklist = data.checklist.map((item, idx) =>
    idx === itemIndex
      ? {
          ...item,
          completed,
          completedByUid: completed ? currentUser.uid : null,
          completedByEmail: completed ? (currentUser.email || "") : null,
          completedByName: completed
            ? currentUser.displayName || currentUser.email || ""
            : null,
          completedAt: completed ? Timestamp.now() : null,
        }
      : item,
  );

  await updateDoc(logRef, {
    checklist: newChecklist,
    lastModifiedBy: {
      uid: currentUser.uid,
      name: currentUser.displayName || currentUser.email || "",
    },
    lastModifiedAt: Timestamp.now(),
  });
};

/** Create a new task template */
export const createTaskTemplate = async ({ careProfileId, templateData }) => {
  const ref = doc(collection(db, `careProfiles/${careProfileId}/taskTemplates`));
  await setDoc(ref, templateData);
  return ref.id;
};

/** Update existing task template */
export const updateTaskTemplate = async ({ careProfileId, templateId, updates }) => {
  const ref = doc(db, `careProfiles/${careProfileId}/taskTemplates/${templateId}`);
  await updateDoc(ref, updates);
};

/** Delete task template */
export const deleteTaskTemplate = async ({ careProfileId, templateId }) => {
  const ref = doc(db, `careProfiles/${careProfileId}/taskTemplates/${templateId}`);
  await deleteDoc(ref);
}; 